import { Canvas, useFrame } from "@react-three/fiber";
import { Points, PointMaterial, Line } from "@react-three/drei";
import { useMemo, useRef } from "react";
import * as THREE from "three";

function NetworkField() {
  const pointsRef = useRef();
  const groupRef = useRef();

  const particleCount = 90;
  const connectionDistance = 2.2;

  const points = useMemo(() => {
    return Array.from({ length: particleCount }, () => [
      (Math.random() - 0.5) * 14,
      (Math.random() - 0.5) * 8,
      (Math.random() - 0.5) * 6,
    ]);
  }, []);

  const connections = useMemo(() => {
    const lines = [];

    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const a = new THREE.Vector3(...points[i]);
        const b = new THREE.Vector3(...points[j]);

        if (a.distanceTo(b) < connectionDistance) {
          lines.push([points[i], points[j]]);
        }
      }
    }

    return lines;
  }, [points]);

  const positions = useMemo(() => {
    return new Float32Array(points.flat());
  }, [points]);

  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();

    groupRef.current.rotation.y = elapsed * 0.035;
    groupRef.current.rotation.x = Math.sin(elapsed * 0.18) * 0.08;

    groupRef.current.position.x = state.pointer.x * 0.5;
    groupRef.current.position.y = state.pointer.y * 0.25;

    pointsRef.current.rotation.z = elapsed * 0.015;
  });

  return (
    <group ref={groupRef}>
      <Points ref={pointsRef} positions={positions} stride={3}>
        <PointMaterial
          transparent
          color="#ffffff"
          size={0.045}
          sizeAttenuation
          depthWrite={false}
          opacity={0.8}
        />
      </Points>

      {connections.map((line, index) => (
        <Line
          key={index}
          points={line}
          color="#ffffff"
          transparent
          opacity={0.12}
          lineWidth={1}
        />
      ))}
    </group>
  );
}

function FloatingGrid() {
  const gridRef = useRef();

  useFrame((state) => {
    const elapsed = state.clock.getElapsedTime();
    gridRef.current.position.z = Math.sin(elapsed * 0.25) * 0.25;
    gridRef.current.rotation.z = elapsed * 0.015;
  });

  return (
    <gridHelper
      ref={gridRef}
      args={[22, 44, "#ffffff", "#ffffff"]}
      position={[0, -3.2, -3]}
      rotation={[Math.PI / 2, 0, 0]}
      material-opacity={0.08}
      material-transparent
    />
  );
}

export default function ThreeBackground() {
  return (
    <div className="three-bg">
      <Canvas
        camera={{ position: [0, 0, 8], fov: 60 }}
        gl={{ antialias: true, alpha: true }}
      >
        <ambientLight intensity={0.6} />
        <fog attach="fog" args={["#050505", 8, 18]} />

        <NetworkField />
        <FloatingGrid />
      </Canvas>
    </div>
  );
}